import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import {ListsModel} from "../models/view/listsModel";
import {NetworkStatus, useMutation, useQuery} from "@apollo/client";
import {contentService} from "../api/contentService";
import {useAppContext} from "./useAppContext";
import {identity} from "../utils/helpers";
import {BsCheck2} from "react-icons/bs";
import {useListItemTransactionState} from "./useListItemTransactionState";
import {ListType} from "../definitions/types";
import {ListItemOperationType} from "../definitions/enums";
import {useForm} from "react-hook-form";
import {FaHeart, FaRegHeart} from "react-icons/fa";

const errorPolicy = "all";

export const useListsQuery = () => {
    const {accountId} = identity();
    const {productId, catalogId} = useAppContext();
    const [show, setShow] = useState<boolean>(false);
    const [showForm, setShowForm] = useState<boolean>(false);
    const {set: setListItemTransaction} = useListItemTransactionState();
    const form = useForm<{ name: string }>({reValidateMode: 'onSubmit'});

    const [createListAsync, {
        loading: createListLoading,
        reset: createListReset
    }] = useMutation(contentService.lists.create, {errorPolicy});
    const [createListItemAsync, {
        loading: createListItemLoading,
        reset: createListItemReset
    }] = useMutation(contentService.lists.items.create, {errorPolicy});
    const [deleteListItemAsync, {
        loading: deleteListItemLoading,
        reset: deleteListItemReset
    }] = useMutation(contentService.lists.items.delete, {errorPolicy});

    const {
        data,
        error,
        loading: listsQueryLoading,
        networkStatus: listsQueryNetworkStatus,
        refetch: refetchLists
    } = useQuery(contentService.lists.query, {
        variables: {productId, accountId},
        notifyOnNetworkStatusChange: true,
        errorPolicy
    });

    const {
        matches,
        hasMatches,
        lists,
        hasLists,
        hasMoreThanOneList
    } = useMemo(() => new ListsModel(data), [data]);

    // Aggregate loading states from all queries and mutations into one loading state
    const loading = useMemo(() => {
        return listsQueryLoading || listsQueryNetworkStatus === NetworkStatus.refetch || createListLoading || createListItemLoading || deleteListItemLoading;
    }, [listsQueryLoading, listsQueryNetworkStatus, createListLoading, createListItemLoading, deleteListItemLoading]);

    // Reset form when the dropdown is closed
    useEffect(() => {
        if (!show && showForm) setShowForm(false);
    }, [show, showForm, setShowForm]);

    // Refetch lists when a mutation is completed, and set the completed transaction state
    const refetchListsAsync = async (listId: number, operation: ListItemOperationType) => {
        refetchLists().then(({data, error}) => {
            if (error) return;

            const listTypes = data.lists
                .filter((list): list is ListType => list !== null)
                .filter(({id}) => id === listId)
                .map(({name, path}) => ({name, path}))
                .slice(0, 1);

            if (listTypes.length === 0) return;

            const {name, path} = listTypes[0];

            setListItemTransaction({
                operation,
                listId,
                name,
                path
            });
        });
    };

    // Add item to the specified list
    const addItemAsync = async (listId: number) => {
        // Reset mutation for sanity
        createListItemReset();

        // Add product to the only list
        const {errors} = await createListItemAsync({
            variables: {listId, productId, accountId, catalogId}
        });

        if (!errors) await refetchListsAsync(listId, ListItemOperationType.Add);
    };

    // Remove item from the specified list
    const removeItemAsync = async (listId: number) => {
        // Reset mutation for sanity
        deleteListItemReset();

        // Remove product from the only list
        const {errors} = await deleteListItemAsync({
            variables: {listId, productId, accountId}
        });

        if (!errors) await refetchListsAsync(listId, ListItemOperationType.Remove);
    };

    // Create a new list and add the product to it when the form is submitted and valid
    const onValid = () => async ({name}: { name: string }) => {
        await createListAndSeedAsync(name);

        // Ensure the dropdown is closed
        setShow(false);

        // Reset the form state after submission
        form.reset();
    };

    // Create the default 'Favorites' list and add the product to it
    const createListAndSeedAsync = async (name?: string) => {
        // Reset the form state for sanity
        createListReset();

        // Create the default 'Favorites' list
        const {data, errors} = await createListAsync({
            variables: {accountId, catalogId, name: name ?? "Favorites"}
        });

        // Verify mutation was successful, if not, return
        if (errors) return;

        // Parse the list id from the response
        const listId = data?.payload?.id;

        // Verify mutation was successful
        if (!listId) return;

        // Add product to the list
        await createListItemAsync({variables: {listId, productId, accountId, catalogId}});

        // Refetch lists to update the UI
        await refetchListsAsync(listId, ListItemOperationType.Add);
    };

    return {
        state: {
            loading,
            error
        },
        form: {
            form,
            show: showForm,
            onToggle: () => setShowForm(!showForm),
            onSubmit: form.handleSubmit(onValid()),
            state: {
                isValid: form.formState.isValid,
                isSubmitting: form.formState.isSubmitting || createListLoading
            }
        },
        model: {
            lists,
            isMatch: (listId: number) => matches.includes(listId)
        },
        dropDownButton: {
            title: hasMatches ? <><FaHeart size={30} className="text-dark" aria-label='saved to favorites'/><BsCheck2
                    aria-hidden className="position-absolute top-50 start-50 translate-middle text-white" size={20}/></> :
                <FaRegHeart size={30} className="text-dark" aria-label='save to favorites'/>,
            show,
            onToggle: async () => {
                // Prevent multiple clicks
                if (loading) return;

                // Verify if there are more than one list, if so, toggle the dropdown
                if (hasMoreThanOneList) {
                    setShow(!show);
                    return;
                }

                // Verify if there is only one list
                if (hasLists) {
                    // Get the list id from the only list
                    const listId = lists[0].id;

                    if (matches.includes(listId)) {
                        await removeItemAsync(listId);
                    } else {
                        await addItemAsync(listId);
                    }
                } else {
                    await createListAndSeedAsync();
                }
            }
        },
        dropDownItem: {
            toggleItem: async (listId: number) => {
                // Prevent multiple clicks
                if (loading) return;

                // Ensure the dropdown is closed
                setShow(false);

                // If the item is already in the list, remove it, otherwise add it
                matches.includes(listId) ? await removeItemAsync(listId) : await addItemAsync(listId);
            }
        }
    }
}